import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";
import NotesIcon from "@mui/icons-material/Notes";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SmsIcon from "@mui/icons-material/Sms";
import WifiIcon from "@mui/icons-material/Wifi";
import ShareIcon from "@mui/icons-material/Share";
import PlaceIcon from "@mui/icons-material/Place";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import EventIcon from "@mui/icons-material/Event";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";

type QRMode =
  | "url"
  | "text"
  | "email"
  | "phone"
  | "sms"
  | "wifi"
  | "whatsapp"
  | "location"
  | "contact"
  | "event"
  | "social";
type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";
type WifiSecurity = "WPA" | "WEP" | "nopass";
type SocialPlatform =
  | "github"
  | "linkedin"
  | "x"
  | "instagram"
  | "youtube"
  | "facebook"
  | "threads"
  | "telegram"
  | "tiktok"
  | "medium"
  | "devto";

const DEFAULT_URL = "https://www.shashi-sharma.com.np";
const DEFAULT_TEXT = "Hello from Shashi Sharma's QR generator";

const escapeWifiValue = (value: string) => value.replace(/([\\;,:"])/g, "\\$1");

const escapeVCardValue = (value: string) =>
  value.replace(/\n/g, "\\n").replace(/[;,]/g, "\\$&");

const escapeICSValue = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const formatICSDate = (value: string) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const pad = (input: number) => String(input).padStart(2, "0");

  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds(),
  )}Z`;
};

const formatFilename = (mode: QRMode) =>
  `qr-${mode}-${new Date().toISOString().slice(0, 10)}.png`;

const qrFaqItems = [
  {
    question: "What can I create with this QR code generator?",
    answer:
      "You can generate QR codes for website URLs, plain text, email, phone numbers, SMS, Wi-Fi credentials, WhatsApp links, map locations, vCard contacts, events, and social profiles.",
  },
  {
    question: "Is this QR generator free to use?",
    answer:
      "Yes. The generator runs directly in the browser and lets you create and download QR codes without a server-side signup flow.",
  },
  {
    question: "Can I add my brand logo inside the QR code?",
    answer:
      "Yes. You can upload a brand icon or logo, preview it in the center of the QR code, and download the finished PNG with the logo overlay included.",
  },
  {
    question: "Does this page upload my QR data to a server?",
    answer:
      "No. QR code generation, logo compositing, previewing, and downloading are handled client-side in the browser.",
  },
] as const;

const qrUseCases = [
  "Marketing campaign links and landing pages",
  "Restaurant, cafe, and event Wi-Fi sharing",
  "Digital business cards and contact sharing",
  "Social profile promotion and creator pages",
  "Event invites, schedules, and location check-ins",
  "WhatsApp, phone, SMS, and email actions",
];

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image."));
    image.src = src;
  });

const drawRoundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - safeRadius,
    y + height,
  );
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const QRGenerator: React.FC = () => {
  const [mode, setMode] = useState<QRMode>("url");
  const [url, setUrl] = useState(DEFAULT_URL);
  const [text, setText] = useState(DEFAULT_TEXT);
  const [email, setEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [phone, setPhone] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [wifiSecurity, setWifiSecurity] = useState<WifiSecurity>("WPA");
  const [wifiHidden, setWifiHidden] = useState(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [whatsAppMessage, setWhatsAppMessage] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactCompany, setContactCompany] = useState("");
  const [contactJobTitle, setContactJobTitle] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactWebsite, setContactWebsite] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [eventStart, setEventStart] = useState("");
  const [eventEnd, setEventEnd] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [socialPlatform, setSocialPlatform] =
    useState<SocialPlatform>("github");
  const [socialHandle, setSocialHandle] = useState("");
  const [size, setSize] = useState(280);
  const [margin, setMargin] = useState(2);
  const [errorCorrectionLevel, setErrorCorrectionLevel] =
    useState<ErrorCorrectionLevel>("M");
  const [darkColor, setDarkColor] = useState("#111827");
  const [lightColor, setLightColor] = useState("#ffffff");
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoScale, setLogoScale] = useState(18);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const qrStructuredData = useMemo(
    () =>
      JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Free QR Code Generator",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Any",
          browserRequirements: "Requires JavaScript and a modern browser",
          url: `${window.location.origin}/qr-generator`,
          description:
            "Free online QR code generator for URLs, Wi-Fi, vCard contacts, events, social profiles, WhatsApp, phone numbers, SMS, and branded QR codes with logo upload.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          creator: {
            "@type": "Person",
            name: "Shashi Sharma",
            url: "https://www.shashi-sharma.com.np",
          },
          featureList: [
            "Free QR code generation",
            "URL QR codes",
            "Wi-Fi QR codes",
            "vCard contact QR codes",
            "Event QR codes",
            "Social media QR codes",
            "Logo upload for branded QR codes",
            "PNG download",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: qrFaqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.shashi-sharma.com.np/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Utilities",
              item: "https://www.shashi-sharma.com.np/qr-generator",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "QR Generator",
              item: "https://www.shashi-sharma.com.np/qr-generator",
            },
          ],
        },
      ]),
    [],
  );

  useEffect(() => {
    updateMetaTags({
      title:
        "Free QR Code Generator for URL, Wi‑Fi, vCard, Event & Social | Shashi Sharma",
      description:
        "Free online QR code generator for URLs, Wi‑Fi, contacts, events, WhatsApp, social media and branded QR codes with logo upload. Create and download QR PNGs instantly.",
      keywords:
        "free qr code generator, qr code generator online, wifi qr code generator, vcard qr code generator, event qr code, social media qr code, branded qr code with logo, whatsapp qr code, contact qr code, url qr code",
      canonical: `${window.location.origin}/qr-generator`,
      ogTitle: "Free QR Code Generator for URL, Wi‑Fi, vCard, Event & Social",
      ogDescription:
        "Create QR codes for links, Wi‑Fi, contacts, events, social profiles, WhatsApp and more. Add your own brand icon and download PNG instantly.",
      ogUrl: `${window.location.origin}/qr-generator`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary",
      twitterTitle:
        "Free QR Code Generator for URL, Wi‑Fi, vCard, Event & Social",
      twitterDescription:
        "Generate QR codes online for URLs, Wi‑Fi, vCards, events, social profiles and branded campaigns with logo upload.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData: qrStructuredData,
    });
  }, [qrStructuredData]);

  const { payload, validationMessage } = useMemo(() => {
    switch (mode) {
      case "url": {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
          return {
            payload: "",
            validationMessage: "Add a URL to generate the QR code.",
          };
        }

        const normalizedUrl = /^https?:\/\//i.test(trimmedUrl)
          ? trimmedUrl
          : `https://${trimmedUrl}`;

        return { payload: normalizedUrl, validationMessage: "" };
      }

      case "text": {
        const trimmedText = text.trim();
        return {
          payload: trimmedText,
          validationMessage: trimmedText
            ? ""
            : "Add some text to generate the QR code.",
        };
      }

      case "email": {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
          return {
            payload: "",
            validationMessage: "Add an email address to continue.",
          };
        }

        const emailParams = new URLSearchParams();
        if (emailSubject.trim()) {
          emailParams.set("subject", emailSubject.trim());
        }
        if (emailBody.trim()) {
          emailParams.set("body", emailBody.trim());
        }

        const query = emailParams.toString();
        return {
          payload: `mailto:${trimmedEmail}${query ? `?${query}` : ""}`,
          validationMessage: "",
        };
      }

      case "phone": {
        const trimmedPhone = phone.trim();
        return {
          payload: trimmedPhone ? `tel:${trimmedPhone}` : "",
          validationMessage: trimmedPhone
            ? ""
            : "Add a phone number to generate the QR code.",
        };
      }

      case "sms": {
        const trimmedPhone = phone.trim();
        if (!trimmedPhone) {
          return {
            payload: "",
            validationMessage: "Add a phone number for the SMS QR code.",
          };
        }

        return {
          payload: `SMSTO:${trimmedPhone}:${smsMessage.trim()}`,
          validationMessage: "",
        };
      }

      case "wifi": {
        const trimmedSsid = wifiSsid.trim();
        if (!trimmedSsid) {
          return {
            payload: "",
            validationMessage: "Add the Wi-Fi name (SSID) to continue.",
          };
        }

        return {
          payload: `WIFI:T:${wifiSecurity};S:${escapeWifiValue(trimmedSsid)};P:${escapeWifiValue(
            wifiPassword,
          )};H:${wifiHidden ? "true" : "false"};;`,
          validationMessage: "",
        };
      }

      case "whatsapp": {
        const trimmedNumber = whatsAppNumber.trim();
        if (!trimmedNumber) {
          return {
            payload: "",
            validationMessage: "Add a WhatsApp number to continue.",
          };
        }

        const digits = trimmedNumber.replace(/[^\d+]/g, "");
        const params = new URLSearchParams();
        if (whatsAppMessage.trim()) {
          params.set("text", whatsAppMessage.trim());
        }

        const query = params.toString();
        return {
          payload: `https://wa.me/${digits.replace(/^\+/, "")}${query ? `?${query}` : ""}`,
          validationMessage: "",
        };
      }

      case "location": {
        const trimmedLatitude = latitude.trim();
        const trimmedLongitude = longitude.trim();

        if (!trimmedLatitude || !trimmedLongitude) {
          return {
            payload: "",
            validationMessage: "Add both latitude and longitude.",
          };
        }

        const lat = Number(trimmedLatitude);
        const lng = Number(trimmedLongitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          return {
            payload: "",
            validationMessage: "Latitude and longitude must be valid numbers.",
          };
        }

        return {
          payload: `geo:${lat},${lng}`,
          validationMessage: "",
        };
      }

      case "contact": {
        const firstName = contactFirstName.trim();
        const lastName = contactLastName.trim();
        const fullName = [firstName, lastName].filter(Boolean).join(" ");

        if (!fullName && !contactPhone.trim() && !contactEmail.trim()) {
          return {
            payload: "",
            validationMessage:
              "Add at least a name, phone number, or email for the contact card.",
          };
        }

        const vcardLines = [
          "BEGIN:VCARD",
          "VERSION:3.0",
          `N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`,
          fullName ? `FN:${escapeVCardValue(fullName)}` : "",
          contactCompany.trim()
            ? `ORG:${escapeVCardValue(contactCompany.trim())}`
            : "",
          contactJobTitle.trim()
            ? `TITLE:${escapeVCardValue(contactJobTitle.trim())}`
            : "",
          contactPhone.trim()
            ? `TEL:${escapeVCardValue(contactPhone.trim())}`
            : "",
          contactEmail.trim()
            ? `EMAIL:${escapeVCardValue(contactEmail.trim())}`
            : "",
          contactWebsite.trim()
            ? `URL:${escapeVCardValue(contactWebsite.trim())}`
            : "",
          contactAddress.trim()
            ? `ADR:;;${escapeVCardValue(contactAddress.trim())};;;;`
            : "",
          "END:VCARD",
        ].filter(Boolean);

        return {
          payload: vcardLines.join("\n"),
          validationMessage: "",
        };
      }

      case "event": {
        const trimmedTitle = eventTitle.trim();
        const formattedStart = formatICSDate(eventStart);
        const formattedEnd = formatICSDate(eventEnd);

        if (!trimmedTitle || !formattedStart || !formattedEnd) {
          return {
            payload: "",
            validationMessage: "Add an event title, start time, and end time.",
          };
        }

        if (new Date(eventEnd).getTime() <= new Date(eventStart).getTime()) {
          return {
            payload: "",
            validationMessage: "Event end time must be after the start time.",
          };
        }

        const eventLines = [
          "BEGIN:VCALENDAR",
          "VERSION:2.0",
          "BEGIN:VEVENT",
          `SUMMARY:${escapeICSValue(trimmedTitle)}`,
          `DTSTART:${formattedStart}`,
          `DTEND:${formattedEnd}`,
          eventLocation.trim()
            ? `LOCATION:${escapeICSValue(eventLocation.trim())}`
            : "",
          eventDescription.trim()
            ? `DESCRIPTION:${escapeICSValue(eventDescription.trim())}`
            : "",
          "END:VEVENT",
          "END:VCALENDAR",
        ].filter(Boolean);

        return {
          payload: eventLines.join("\n"),
          validationMessage: "",
        };
      }

      case "social": {
        const trimmedHandle = socialHandle.trim();
        if (!trimmedHandle) {
          return {
            payload: "",
            validationMessage: "Add a username or profile URL.",
          };
        }

        if (/^https?:\/\//i.test(trimmedHandle)) {
          return {
            payload: trimmedHandle,
            validationMessage: "",
          };
        }

        const normalizedHandle = trimmedHandle
          .replace(/^@/, "")
          .replace(/^\/+/, "");
        const platformBaseMap: Record<SocialPlatform, string> = {
          github: "https://github.com/",
          linkedin: "https://www.linkedin.com/in/",
          x: "https://x.com/",
          instagram: "https://instagram.com/",
          youtube: "https://www.youtube.com/@",
          facebook: "https://facebook.com/",
          threads: "https://www.threads.net/@",
          telegram: "https://t.me/",
          tiktok: "https://www.tiktok.com/@",
          medium: "https://medium.com/@",
          devto: "https://dev.to/",
        };

        return {
          payload: `${platformBaseMap[socialPlatform]}${normalizedHandle}`,
          validationMessage: "",
        };
      }

      default:
        return { payload: "", validationMessage: "" };
    }
  }, [
    email,
    emailBody,
    emailSubject,
    mode,
    phone,
    smsMessage,
    text,
    url,
    wifiHidden,
    wifiPassword,
    wifiSecurity,
    wifiSsid,
    whatsAppMessage,
    whatsAppNumber,
    latitude,
    longitude,
    contactAddress,
    contactCompany,
    contactEmail,
    contactFirstName,
    contactJobTitle,
    contactLastName,
    contactPhone,
    contactWebsite,
    eventDescription,
    eventEnd,
    eventLocation,
    eventStart,
    eventTitle,
    socialHandle,
    socialPlatform,
  ]);

  useEffect(() => {
    let active = true;

    if (!payload || validationMessage) {
      setQrDataUrl("");
      return undefined;
    }

    const generateQRCode = async () => {
      try {
        const baseQrDataUrl = await QRCode.toDataURL(payload, {
          width: size,
          margin,
          errorCorrectionLevel,
          color: {
            dark: darkColor,
            light: lightColor,
          },
        });

        if (!logoDataUrl) {
          if (active) {
            setQrDataUrl(baseQrDataUrl);
          }
          return;
        }

        const [qrImage, logoImage] = await Promise.all([
          loadImage(baseQrDataUrl),
          loadImage(logoDataUrl),
        ]);

        const canvas = document.createElement("canvas");
        const canvasSize = qrImage.naturalWidth || size;
        canvas.width = canvasSize;
        canvas.height = canvasSize;

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Canvas is not available.");
        }

        context.drawImage(qrImage, 0, 0, canvas.width, canvas.height);

        const iconSize = Math.round((canvas.width * logoScale) / 100);
        const padding = Math.round(iconSize * 0.18);
        const badgeSize = iconSize + padding * 2;
        const badgeX = (canvas.width - badgeSize) / 2;
        const badgeY = (canvas.height - badgeSize) / 2;
        const iconX = (canvas.width - iconSize) / 2;
        const iconY = (canvas.height - iconSize) / 2;

        context.fillStyle = "#ffffff";
        context.shadowColor = "rgba(15, 23, 42, 0.18)";
        context.shadowBlur = 18;
        drawRoundedRect(
          context,
          badgeX,
          badgeY,
          badgeSize,
          badgeSize,
          badgeSize * 0.22,
        );
        context.fill();
        context.shadowBlur = 0;

        context.save();
        drawRoundedRect(
          context,
          iconX,
          iconY,
          iconSize,
          iconSize,
          iconSize * 0.18,
        );
        context.clip();
        context.drawImage(logoImage, iconX, iconY, iconSize, iconSize);
        context.restore();

        if (active) {
          setQrDataUrl(canvas.toDataURL("image/png"));
        }
      } catch (_error) {
        if (active) {
          setQrDataUrl("");
        }
      }
    };

    generateQRCode();

    return () => {
      active = false;
    };
  }, [
    darkColor,
    errorCorrectionLevel,
    lightColor,
    margin,
    logoDataUrl,
    logoScale,
    payload,
    size,
    validationMessage,
  ]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setFeedback("Please choose an image file for the QR icon.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setLogoDataUrl(reader.result);
        setFeedback("Brand icon added to the QR preview.");
      }
    };
    reader.onerror = () => {
      setFeedback("Unable to read the selected icon file.");
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleRemoveLogo = () => {
    setLogoDataUrl(null);
    setLogoScale(18);
    setFeedback("Brand icon removed from the QR code.");
  };

  const handleCopy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(message);
    } catch (_error) {
      setFeedback("Clipboard access is not available in this browser.");
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;

    const link = document.createElement("a");
    link.href = qrDataUrl;
    link.download = formatFilename(mode);
    link.click();
  };

  const handleReset = () => {
    setMode("url");
    setUrl(DEFAULT_URL);
    setText(DEFAULT_TEXT);
    setEmail("");
    setEmailSubject("");
    setEmailBody("");
    setPhone("");
    setSmsMessage("");
    setWifiSsid("");
    setWifiPassword("");
    setWifiSecurity("WPA");
    setWifiHidden(false);
    setWhatsAppNumber("");
    setWhatsAppMessage("");
    setLatitude("");
    setLongitude("");
    setContactFirstName("");
    setContactLastName("");
    setContactCompany("");
    setContactJobTitle("");
    setContactPhone("");
    setContactEmail("");
    setContactWebsite("");
    setContactAddress("");
    setEventTitle("");
    setEventStart("");
    setEventEnd("");
    setEventLocation("");
    setEventDescription("");
    setSocialPlatform("github");
    setSocialHandle("");
    setSize(280);
    setMargin(2);
    setErrorCorrectionLevel("M");
    setDarkColor("#111827");
    setLightColor("#ffffff");
    setLogoDataUrl(null);
    setLogoScale(18);
    setFeedback("QR generator reset to defaults.");
  };

  const modeChips: Array<{
    value: QRMode;
    label: string;
    icon: React.ReactElement;
  }> = [
    { value: "url", label: "Link", icon: <LinkIcon fontSize="small" /> },
    { value: "text", label: "Text", icon: <NotesIcon fontSize="small" /> },
    { value: "email", label: "Email", icon: <EmailIcon fontSize="small" /> },
    {
      value: "phone",
      label: "Phone",
      icon: <PhoneIphoneIcon fontSize="small" />,
    },
    { value: "sms", label: "SMS", icon: <SmsIcon fontSize="small" /> },
    { value: "wifi", label: "Wi-Fi", icon: <WifiIcon fontSize="small" /> },
    {
      value: "whatsapp",
      label: "WhatsApp",
      icon: <ShareIcon fontSize="small" />,
    },
    {
      value: "location",
      label: "Location",
      icon: <PlaceIcon fontSize="small" />,
    },
    {
      value: "contact",
      label: "Contact",
      icon: <ContactPageIcon fontSize="small" />,
    },
    {
      value: "event",
      label: "Event",
      icon: <EventIcon fontSize="small" />,
    },
    {
      value: "social",
      label: "Social",
      icon: <ShareIcon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="h1"
            variant="h4"
            sx={{ fontWeight: 800, mb: 0.75 }}
          >
            Free QR Code Generator
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 920 }}
          >
            Create QR codes for URLs, Wi‑Fi, contacts, events, social profiles,
            WhatsApp and more. Add a brand icon, customize colors, and download
            a PNG instantly.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(360px, 420px) minmax(0, 1fr)",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Stack
            spacing={2}
            sx={{
              order: { xs: 1, sm: 1 },
              position: { sm: "sticky" },
              top: { sm: 88 },
            }}
          >
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Preview
                    </Typography>
                    <Chip
                      size="small"
                      icon={<QrCode2Icon />}
                      label={mode.toUpperCase()}
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>

                  <Box
                    sx={{
                      minHeight: { xs: 260, sm: 320 },
                      borderRadius: 3,
                      border: "1px dashed",
                      borderColor: validationMessage
                        ? "warning.main"
                        : "divider",
                      backgroundColor: "background.default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                    }}
                  >
                    {qrDataUrl ? (
                      <Box
                        component="img"
                        src={qrDataUrl}
                        alt="Generated QR code preview"
                        sx={{
                          width: "100%",
                          maxWidth: { xs: 220, sm: 320 },
                          borderRadius: 2,
                          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                        }}
                      />
                    ) : (
                      <Stack
                        spacing={1.25}
                        alignItems="center"
                        textAlign="center"
                      >
                        <QrCode2Icon
                          sx={{ fontSize: 52, color: "text.disabled" }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          QR preview unavailable
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {validationMessage && (
                    <Alert severity="warning">{validationMessage}</Alert>
                  )}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      disabled={!qrDataUrl}
                    >
                      Download PNG
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopy(payload, "QR payload copied.")}
                      disabled={!payload}
                    >
                      Copy payload
                    </Button>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() =>
                        handleCopy(window.location.href, "Page URL copied.")
                      }
                    >
                      Copy page link
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {feedback && (
              <Alert severity="success" onClose={() => setFeedback(null)}>
                {feedback}
              </Alert>
            )}
          </Stack>

          <Card variant="outlined" sx={{ order: { xs: 2, sm: 2 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1.5 }}
                  >
                    Type
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {modeChips.map((chip) => (
                      <Chip
                        key={chip.value}
                        icon={chip.icon}
                        label={chip.label}
                        clickable
                        color={mode === chip.value ? "primary" : "default"}
                        variant={mode === chip.value ? "filled" : "outlined"}
                        onClick={() => setMode(chip.value)}
                      />
                    ))}
                  </Stack>
                </Box>

                <Divider />

                <Stack spacing={2.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Content
                  </Typography>

                  {mode === "url" && (
                    <TextField
                      fullWidth
                      label="Website URL"
                      placeholder="https://example.com"
                      value={url}
                      onChange={(event) => setUrl(event.target.value)}
                    />
                  )}

                  {mode === "text" && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Text content"
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      placeholder="Add any message, note, or identifier"
                    />
                  )}

                  {mode === "email" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        type="email"
                        label="Email address"
                        placeholder="hello@example.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Subject"
                        value={emailSubject}
                        onChange={(event) =>
                          setEmailSubject(event.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Message body"
                        value={emailBody}
                        onChange={(event) => setEmailBody(event.target.value)}
                      />
                    </Stack>
                  )}

                  {mode === "phone" && (
                    <TextField
                      fullWidth
                      label="Phone number"
                      placeholder="+9779812345678"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                    />
                  )}

                  {mode === "sms" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Phone number"
                        placeholder="+9779812345678"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="SMS message"
                        value={smsMessage}
                        onChange={(event) => setSmsMessage(event.target.value)}
                      />
                    </Stack>
                  )}

                  {mode === "wifi" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Wi-Fi name (SSID)"
                        value={wifiSsid}
                        onChange={(event) => setWifiSsid(event.target.value)}
                      />
                      <FormControl fullWidth>
                        <InputLabel id="wifi-security-label">
                          Security
                        </InputLabel>
                        <Select
                          labelId="wifi-security-label"
                          label="Security"
                          value={wifiSecurity}
                          onChange={(event) =>
                            setWifiSecurity(event.target.value as WifiSecurity)
                          }
                        >
                          <MenuItem value="WPA">WPA / WPA2</MenuItem>
                          <MenuItem value="WEP">WEP</MenuItem>
                          <MenuItem value="nopass">Open network</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Password"
                        type={wifiSecurity === "nopass" ? "text" : "password"}
                        value={wifiPassword}
                        disabled={wifiSecurity === "nopass"}
                        onChange={(event) =>
                          setWifiPassword(event.target.value)
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={wifiHidden}
                            onChange={(event) =>
                              setWifiHidden(event.target.checked)
                            }
                          />
                        }
                        label="Hidden network"
                      />
                    </Stack>
                  )}

                  {mode === "whatsapp" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="WhatsApp number"
                        placeholder="+9779812345678"
                        value={whatsAppNumber}
                        onChange={(event) =>
                          setWhatsAppNumber(event.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Pre-filled message"
                        value={whatsAppMessage}
                        onChange={(event) =>
                          setWhatsAppMessage(event.target.value)
                        }
                      />
                    </Stack>
                  )}

                  {mode === "location" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Latitude"
                        placeholder="27.7172"
                        value={latitude}
                        onChange={(event) => setLatitude(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Longitude"
                        placeholder="85.3240"
                        value={longitude}
                        onChange={(event) => setLongitude(event.target.value)}
                      />
                    </Stack>
                  )}

                  {mode === "contact" && (
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          fullWidth
                          label="First name"
                          value={contactFirstName}
                          onChange={(event) =>
                            setContactFirstName(event.target.value)
                          }
                        />
                        <TextField
                          fullWidth
                          label="Last name"
                          value={contactLastName}
                          onChange={(event) =>
                            setContactLastName(event.target.value)
                          }
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        label="Company"
                        value={contactCompany}
                        onChange={(event) =>
                          setContactCompany(event.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        label="Job title"
                        value={contactJobTitle}
                        onChange={(event) =>
                          setContactJobTitle(event.target.value)
                        }
                      />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          fullWidth
                          label="Phone"
                          value={contactPhone}
                          onChange={(event) =>
                            setContactPhone(event.target.value)
                          }
                        />
                        <TextField
                          fullWidth
                          type="email"
                          label="Email"
                          value={contactEmail}
                          onChange={(event) =>
                            setContactEmail(event.target.value)
                          }
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        label="Website"
                        placeholder="https://example.com"
                        value={contactWebsite}
                        onChange={(event) =>
                          setContactWebsite(event.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Address"
                        value={contactAddress}
                        onChange={(event) =>
                          setContactAddress(event.target.value)
                        }
                      />
                    </Stack>
                  )}

                  {mode === "event" && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Event title"
                        value={eventTitle}
                        onChange={(event) => setEventTitle(event.target.value)}
                      />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          fullWidth
                          type="datetime-local"
                          label="Start"
                          value={eventStart}
                          onChange={(event) =>
                            setEventStart(event.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          fullWidth
                          type="datetime-local"
                          label="End"
                          value={eventEnd}
                          onChange={(event) => setEventEnd(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        label="Location"
                        value={eventLocation}
                        onChange={(event) =>
                          setEventLocation(event.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Description"
                        value={eventDescription}
                        onChange={(event) =>
                          setEventDescription(event.target.value)
                        }
                      />
                    </Stack>
                  )}

                  {mode === "social" && (
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel id="social-platform-label">
                          Platform
                        </InputLabel>
                        <Select
                          labelId="social-platform-label"
                          label="Platform"
                          value={socialPlatform}
                          onChange={(event) =>
                            setSocialPlatform(
                              event.target.value as SocialPlatform,
                            )
                          }
                        >
                          <MenuItem value="github">GitHub</MenuItem>
                          <MenuItem value="linkedin">LinkedIn</MenuItem>
                          <MenuItem value="x">X</MenuItem>
                          <MenuItem value="instagram">Instagram</MenuItem>
                          <MenuItem value="youtube">YouTube</MenuItem>
                          <MenuItem value="facebook">Facebook</MenuItem>
                          <MenuItem value="threads">Threads</MenuItem>
                          <MenuItem value="telegram">Telegram</MenuItem>
                          <MenuItem value="tiktok">TikTok</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="devto">DEV Community</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Username or full URL"
                        placeholder="sharmashashi"
                        value={socialHandle}
                        onChange={(event) =>
                          setSocialHandle(event.target.value)
                        }
                      />
                    </Stack>
                  )}
                </Stack>

                <Divider />

                <Stack spacing={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Appearance
                  </Typography>

                  <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Brand icon
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                    >
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                      >
                        {logoDataUrl ? "Replace icon" : "Upload icon"}
                        <Box
                          component="input"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          sx={{ display: "none" }}
                        />
                      </Button>
                      {logoDataUrl && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={handleRemoveLogo}
                        >
                          Remove icon
                        </Button>
                      )}
                    </Stack>

                    {logoDataUrl && (
                      <Stack spacing={1.25}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            overflow: "hidden",
                            backgroundColor: "background.paper",
                          }}
                        >
                          <Box
                            component="img"
                            src={logoDataUrl}
                            alt="Brand icon preview"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            Icon size: {logoScale}%
                          </Typography>
                          <Slider
                            min={12}
                            max={24}
                            step={1}
                            value={logoScale}
                            onChange={(_event, value) =>
                              setLogoScale(value as number)
                            }
                            valueLabelDisplay="auto"
                          />
                        </Box>
                      </Stack>
                    )}
                  </Stack>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Size: {size}px
                    </Typography>
                    <Slider
                      min={160}
                      max={512}
                      step={8}
                      value={size}
                      onChange={(_event, value) => setSize(value as number)}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Margin: {margin}
                    </Typography>
                    <Slider
                      min={0}
                      max={8}
                      step={1}
                      value={margin}
                      onChange={(_event, value) => setMargin(value as number)}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel id="error-level-label">
                      Error correction
                    </InputLabel>
                    <Select
                      labelId="error-level-label"
                      label="Error correction"
                      value={errorCorrectionLevel}
                      onChange={(event) =>
                        setErrorCorrectionLevel(
                          event.target.value as ErrorCorrectionLevel,
                        )
                      }
                    >
                      <MenuItem value="L">Low</MenuItem>
                      <MenuItem value="M">Medium</MenuItem>
                      <MenuItem value="Q">Quartile</MenuItem>
                      <MenuItem value="H">High</MenuItem>
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Foreground color
                      </Typography>
                      <Box
                        component="input"
                        type="color"
                        value={darkColor}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => setDarkColor(event.target.value)}
                        sx={{
                          width: "100%",
                          height: 54,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          backgroundColor: "background.paper",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Background color
                      </Typography>
                      <Box
                        component="input"
                        type="color"
                        value={lightColor}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => setLightColor(event.target.value)}
                        sx={{
                          width: "100%",
                          height: 54,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          backgroundColor: "background.paper",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box component="section" sx={{ mt: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    component="h2"
                    variant="h5"
                    sx={{ fontWeight: 800, mb: 1 }}
                  >
                    QR code generator for business, creator and everyday use
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This page is built as a free online QR code generator for
                    fast, practical use cases. You can generate QR codes for
                    website links, menu pages, contact cards, Wi‑Fi sharing,
                    event check-ins, creator profiles and customer support flows
                    without leaving the browser.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      component="h3"
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1.25 }}
                    >
                      Popular QR use cases
                    </Typography>
                    <Stack spacing={1}>
                      {qrUseCases.map((item) => (
                        <Typography
                          key={item}
                          variant="body2"
                          color="text.secondary"
                        >
                          • {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography
                      component="h3"
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1.25 }}
                    >
                      Why this QR generator is useful
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        • Generates QR codes directly in the browser for faster
                        creation.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Supports branded QR codes with an optional center icon
                        or logo.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Covers high-intent formats like Wi‑Fi, vCard, event
                        and social QR codes.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Lets visitors customize colors, size, spacing and
                        error correction before download.
                      </Typography>
                    </Stack>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    component="h2"
                    variant="h5"
                    sx={{ fontWeight: 800, mb: 2 }}
                  >
                    Frequently asked questions
                  </Typography>
                  <Stack spacing={2}>
                    {qrFaqItems.map((item) => (
                      <Box key={item.question}>
                        <Typography
                          component="h3"
                          variant="subtitle1"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        >
                          {item.question}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.answer}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default QRGenerator;
